/**
 * The button that runs a save or another action, with its state on it:
 * disabled with a spinner while it runs, a check and "Saved" for two seconds
 * once it succeeded, and a short shake when it failed (the button is usable
 * again at once, and the form keeps what was typed). Pair it with
 * `buttonState(mutation)`. The toast and the form's error banner say what
 * happened; the button only shows it.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';

const SAVED_MS = 2000;
const SHAKE_MS = 450;

export interface ButtonState {
  pending: boolean;
  error: unknown;
  /** Changes on every success, so the "Saved" state shows each time. */
  succeededAt?: number;
}

/** What `ActionButton` needs from a TanStack Query mutation. */
export function buttonState(mutation: { isPending: boolean; isSuccess: boolean; error: unknown; submittedAt: number }): ButtonState {
  return {
    pending: mutation.isPending,
    error: mutation.error,
    succeededAt: mutation.isSuccess ? mutation.submittedAt : undefined,
  };
}

/** "Saved" for a moment after each success; a shake after each failure. */
function useOutcomeFlash(state: ButtonState) {
  const [done, setDone] = useState(false);
  const [shaking, setShaking] = useState(false);

  useEffect(() => {
    if (state.succeededAt === undefined) return;
    setDone(true);
    const timer = window.setTimeout(() => setDone(false), SAVED_MS);
    return () => window.clearTimeout(timer);
  }, [state.succeededAt]);

  useEffect(() => {
    if (!state.error) return;
    setDone(false);
    setShaking(true);
    const timer = window.setTimeout(() => setShaking(false), SHAKE_MS);
    return () => window.clearTimeout(timer);
  }, [state.error]);

  return { done, shaking };
}

function ButtonContent({
  pending,
  done,
  icon,
  label,
  pendingLabel,
  doneLabel,
}: {
  pending: boolean;
  done: boolean;
  icon: ReactNode;
  label: ReactNode;
  pendingLabel: string;
  doneLabel: string;
}) {
  if (pending) return <><Loader2 aria-hidden="true" className="spin" /> {pendingLabel}</>;
  if (done) return <><Check aria-hidden="true" /> {doneLabel}</>;
  return <>{icon} {label}</>;
}

export function ActionButton({
  state,
  label,
  icon,
  pendingLabel = 'Saving…',
  doneLabel = 'Saved',
  className = 'btn btn-primary',
  type = 'submit',
  disabled = false,
  autoFocus,
  describedBy,
  onClick,
}: {
  state: ButtonState;
  label: ReactNode;
  icon?: ReactNode;
  pendingLabel?: string;
  doneLabel?: string;
  className?: string;
  type?: 'submit' | 'button';
  disabled?: boolean;
  autoFocus?: boolean;
  describedBy?: string;
  onClick?: () => void;
}) {
  const { done, shaking } = useOutcomeFlash(state);
  const flags = `${done ? ' is-done' : ''}${shaking ? ' is-shaking' : ''}`;

  return (
    <button
      type={type}
      className={className + flags}
      disabled={disabled || state.pending}
      aria-busy={state.pending || undefined}
      autoFocus={autoFocus}
      aria-describedby={describedBy}
      onClick={onClick}
    >
      <ButtonContent
        pending={state.pending}
        done={done}
        icon={icon}
        label={label}
        pendingLabel={pendingLabel}
        doneLabel={doneLabel}
      />
    </button>
  );
}
